import { EventClickPopover } from "@/components/agent-triggers/EventClickPopover.tsx";
import { RangeSelectionPopover } from "@/components/agent-triggers/RangeSelectionPopover.tsx";
import { type EventDeepLink, useEventById } from "@/components/calendars/useEventDeepLink.ts";
import { useFocusedEventPopover } from "@/components/calendars/useFocusedEventPopover.ts";
import { useCalendarSurface } from "@/components/calendars/useCalendarSurface.ts";
import { eventDetailPath } from "@/components/calendars/calendar-url.ts";
import { useVisibleCalendarIds } from "@/components/calendars/useVisibleCalendarIds.ts";
import { useLegacyEventSidebarId, useRightSidebar } from "@/components/right-sidebar/hooks.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { toUrnString } from "@/types/urn.ts";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { CanvasHostProps } from "./CanvasHost.tsx";
import { type CanvasActionContextValue, useCanvasActionContext } from "./canvasContext.tsx";
import { useCanvasCalendarNav } from "./useCanvasCalendarNav.ts";

export type CanvasCalendarSurfaceHostProps = Pick<
  CanvasHostProps,
  "calendarIds" | "hiddenCalendarIds" | "onEventClick" | "gridProps" | "containerRef" | "overlay"
>;

/**
 * The shared canvas calendar surface consumed by both home (`Calendar.tsx`) and
 * the thread canvas (via `CanvasPanel`). Owns the interactive surface state —
 * event-click popovers and range-selection overlays — and exposes `hostProps`
 * (the props the canvas host spreads onto the grid) plus `selection` for callers
 * that read the active ranges.
 *
 * Page-level controller concerns (the calendar-visibility dialog and mobile event
 * routing) live in `Calendar.tsx`, which composes them on top of this surface.
 * Mobile event clicks are delegated via the optional `onMobileEventClick`
 * injection so the surface stays agnostic to how the page routes them; a
 * `deepLink` is likewise resolved by the page and handed here only once it names
 * an event.
 *
 * Calendar *focus* links aren't handled here — the selection and the view/date
 * are both shared stores, so `CalendarFocusNavBoundary` writes them directly. An
 * event deep link is different: it has to anchor a popover to a chip on this
 * surface, which is state no store can hold.
 */
export const useCanvasCalendarSurface = ({
  onMobileEventClick,
  selectedEventId = null,
  onSelectEvent,
  deepLink,
  canvas: injectedCanvas,
}: {
  onMobileEventClick?: (eventId: string) => void;
  /**
   * The event the host's URL names, when the host puts its selection in the URL.
   * The popover follows this rather than leading it, so a link, a reload, and
   * the Back gesture all land the same way a click does (BIG-7804).
   */
  selectedEventId?: string | null;
  /**
   * Tell the host an event was selected, or deselected with null. Its presence
   * is what makes this surface URL-driven: without it — the calendar inside a
   * thread's canvas, which has no address of its own to spend — the popover
   * stays this surface's own state, exactly as it was.
   */
  onSelectEvent?: (eventId: string | null) => void;
  /** An event the URL asked to focus (a calendar reminder's notification tap). */
  deepLink?: EventDeepLink;
  /**
   * The canvas to present events on. Required from a page that *provides* the
   * context rather than sitting under it: a component cannot read a context it
   * renders itself, so `Calendar.tsx` hands its actions in directly. Omitted by
   * callers mounted below a provider (`CanvasPanel` on the thread canvas), which
   * read it from context.
   */
  canvas?: CanvasActionContextValue;
} = {}) => {
  const isMobile = useIsMobile();
  const { currentDate, viewType } = useCanvasCalendarNav();
  const { hiddenCalendarIds, calendarIdsForEvents } = useVisibleCalendarIds();
  const contextCanvas = useCanvasActionContext();
  const canvas = injectedCanvas ?? contextCanvas;
  const [, setLocation] = useLocation();

  const calendarAreaRef = useRef<HTMLDivElement>(null);
  const [eventClickTarget, setEventClickTarget] = useState<{
    eventId: string;
    rect: DOMRect;
  } | null>(null);

  /**
   * Put the popover on an event's chip. Says nothing about the address.
   *
   * The rect is read here and not kept: a measurement is only good for the
   * layout it was taken in, and the one time a remembered one would be reached
   * for — coming back to an event the popover is not already on — is exactly
   * when the grid has since moved, so it would place the popover where the chip
   * used to be. An arrival with no chip in hand re-finds it instead.
   */
  const anchorPopover = useCallback((eventId: string, anchor: HTMLElement) => {
    setEventClickTarget({ eventId, rect: anchor.getBoundingClientRect() });
  }, []);

  const handleEventClickAnchored = useCallback(
    (eventId: string, anchor: HTMLElement) => {
      // On mobile, defer to the page's handler (if any) instead of the anchored
      // popover — the page decides how to route the click.
      if (isMobile && onMobileEventClick) {
        onMobileEventClick(eventId);
        return;
      }
      // Anchored before the address is written, not after: the rect is only
      // measurable here, and the popover should be up on the click rather than
      // a navigation later.
      anchorPopover(eventId, anchor);
      onSelectEvent?.(eventId);
    },
    [isMobile, onMobileEventClick, anchorPopover, onSelectEvent],
  );

  const { events, selection, gridProps } = useCalendarSurface({
    date: currentDate,
    viewType,
    calendarIds: calendarIdsForEvents,
  });

  /**
   * Open an event where no chip can be anchored to — the deep link's fallback
   * surface. Presents on the canvas where there is one, and otherwise loads the
   * event's own page (mobile, gate-off).
   */
  const openEventDetail = useCallback(
    (eventId: string) => {
      if (canvas?.resetTo(toUrnString({ type: "source", id: eventId }))) return;
      setLocation(eventDetailPath(eventId));
    },
    [canvas, setLocation],
  );

  // A `?sb_type=eventEdit` URL asks for the event's right-sidebar, which this
  // surface no longer uses — bookmarks, shared links, and history entries still
  // carry it. Present the event on the canvas instead and drop the params, so
  // the address settles on what is actually on screen.
  //
  // Layout effect, not a plain one: the sidebar renders from the same URL, so
  // handing it over after paint would flash the sidebar open and shut. Left
  // alone where there is no canvas (mobile, gate-off), which is still the
  // sidebar's to serve.
  const sidebarEventId = useLegacyEventSidebarId();
  const { closeSidebar } = useRightSidebar();
  useLayoutEffect(() => {
    if (!sidebarEventId || !canvas) return;
    canvas.resetTo(toUrnString({ type: "source", id: sidebarEventId }));
    closeSidebar();
  }, [sidebarEventId, canvas, closeSidebar]);

  const { focusEvent, highlightedInstanceKey } = useFocusedEventPopover({
    containerRef: calendarAreaRef,
    events,
    // Anchors without selecting: this runs to satisfy a selection that already
    // exists, and calling the click handler would write the same address again.
    onAnchored: anchorPopover,
    onUnanchorable: openEventDetail,
  });

  // Dismissing is deselecting where the host owns a selection — the popover is
  // what `/calendar/event/:id` is showing, so closing it has to leave that
  // address or the URL would name something no longer on screen.
  const dismissEventClick = useCallback(() => {
    setEventClickTarget(null);
    onSelectEvent?.(null);
  }, [onSelectEvent]);

  // Route a resolved deep link to the surface that can actually show it.
  const deepLinkedEvent = deepLink?.event;
  const clearDeepLink = deepLink?.clear;
  useEffect(() => {
    if (!deepLinkedEvent || !clearDeepLink) return;
    // Take ownership immediately: from here the focus lives in this surface's
    // state, so re-resolving the same link can't re-open a dismissed popover.
    clearDeepLink();
    const eventId = deepLinkedEvent.gcalId;
    if (!eventId) return;
    // Where the host has an address for a selection, hand the link to it and
    // let the effect below present it. The notification's `?eventId=` param is
    // a way in, not a place to stay, and this is what settles the URL on the
    // event's own path rather than leaving it naming the bare calendar.
    if (onSelectEvent) {
      onSelectEvent(eventId);
      return;
    }
    // Mobile has no anchored popover (the calendar may not even be the visible
    // tab), and a hidden calendar's events are filtered off the grid, so neither
    // will ever produce a chip.
    if (isMobile || hiddenCalendarIds.has(deepLinkedEvent.calendarId)) {
      openEventDetail(eventId);
      return;
    }
    focusEvent(deepLinkedEvent);
  }, [
    deepLinkedEvent,
    clearDeepLink,
    isMobile,
    hiddenCalendarIds,
    focusEvent,
    openEventDetail,
    onSelectEvent,
  ]);

  // The address names an event this surface is not already showing — a pasted
  // link, a reload, a Back into a selection made earlier. Resolve it so the
  // focus machinery has an event to jump to; an id the popover is already on
  // resolves to nothing and costs no fetch.
  const unanchoredSelectionId =
    onSelectEvent && selectedEventId && selectedEventId !== eventClickTarget?.eventId
      ? selectedEventId
      : undefined;
  const { event: selectedEvent, isResolving: isResolvingSelection } =
    useEventById(unanchoredSelectionId);

  // Keep the popover in step with the address, in both directions of "not what
  // is showing": present a newly named event, and take the popover down when the
  // selection is gone.
  useEffect(() => {
    if (!onSelectEvent) return; // this surface owns its own popover
    if (!selectedEventId) {
      setEventClickTarget(null);
      return;
    }
    if (selectedEventId === eventClickTarget?.eventId) return;
    // Mobile renders the event in the column instead of anchoring a popover to a
    // chip it has no room for, so there is nothing to present here.
    if (isMobile) return;
    if (isResolvingSelection) return;
    // Settled with nothing: the event was deleted, never existed, or the
    // request failed. Hand it to the surface that can say so — waiting instead
    // would leave the address naming an event and the calendar showing no
    // popover, no error, and no sign that anything was asked for.
    if (!selectedEvent) {
      openEventDetail(selectedEventId);
      return;
    }
    // A hidden calendar's events are filtered off the grid, so no chip is
    // coming — the same case the deep link hands off for.
    if (hiddenCalendarIds.has(selectedEvent.calendarId)) {
      openEventDetail(selectedEventId);
      return;
    }
    focusEvent(selectedEvent);
  }, [
    onSelectEvent,
    selectedEventId,
    eventClickTarget?.eventId,
    isMobile,
    selectedEvent,
    isResolvingSelection,
    hiddenCalendarIds,
    focusEvent,
    openEventDetail,
  ]);

  const overlay = useMemo(
    () => (
      <>
        {/* Only a range the user drew opens the agent. A calendar focus link
            (an artifact's date, an agent day-range link) seeds the same
            highlight to show the user where something sits — popping the
            "create an event / ask anything" prompt on top of that reads as the
            app asking them to start a thread they never asked for (BIG-7296).
            The highlight and its omnibox chip still stand. */}
        {selection.origin === "drag" && (
          <RangeSelectionPopover
            ranges={selection.ranges}
            onDismiss={selection.clearAll}
            parentRef={calendarAreaRef}
          />
        )}
        {eventClickTarget && (
          <EventClickPopover
            eventId={eventClickTarget.eventId}
            anchorRect={eventClickTarget.rect}
            onDismiss={dismissEventClick}
          />
        )}
      </>
    ),
    [selection.ranges, selection.clearAll, selection.origin, eventClickTarget, dismissEventClick],
  );

  const hostProps = useMemo(
    () =>
      ({
        calendarIds: calendarIdsForEvents,
        hiddenCalendarIds,
        onEventClick: handleEventClickAnchored,
        // The ring is also how the focused event's chip is found in the DOM, so
        // it rides along with the rest of the grid wiring.
        gridProps: { ...gridProps, highlightedInstanceKey },
        containerRef: calendarAreaRef,
        overlay,
      }) satisfies CanvasCalendarSurfaceHostProps,
    [
      calendarIdsForEvents,
      hiddenCalendarIds,
      handleEventClickAnchored,
      gridProps,
      highlightedInstanceKey,
      overlay,
    ],
  );

  return {
    selection,
    hostProps,
  };
};

export type CanvasCalendarSurface = ReturnType<typeof useCanvasCalendarSurface>;
